import { get, set, del } from 'idb-keyval'
import { persistQueryClient } from '@tanstack/query-persist-client-core'
import getPersistableEntities from '../model/cacheWhitelist'

const WHITELIST = getPersistableEntities()
const MAX_CACHED_RECORDS = 100
const MAX_AGE = 24 * 60 * 60 * 1000

function truncateInfiniteData(data) {
    const allItems = data.pages.flatMap(p => p.data || p)
    if (allItems.length <= MAX_CACHED_RECORDS) return data
    // Reconstructed page loses meta.lastId — getNextPageParam returns undefined.
    // Infinite scroll disabled on cached data; background refetch restores pagination.
    return {
        ...data,
        pages: [{ data: allItems.slice(0, MAX_CACHED_RECORDS) }],
        pageParams: [data.pageParams?.[0]],
    }
}

function isNonEmptyData(data) {
    if (data == null) return false
    if (data.pages) return data.pages.flatMap(p => p.data || p).length > 0
    if (Array.isArray(data)) return data.length > 0
    if (data.list != null) return data.list.length > 0  // addHeaders { list, headers } format
    return true
}

function shouldDehydrateQuery(query) {
    if (query.state.status !== 'success') return false
    if (!isNonEmptyData(query.state.data)) return false
    return query.queryKey.some(part => WHITELIST.includes(part))
}

function makePersister(cacheKey) {
    return {
        persistClient: async (client) => {
            const safe = {
                ...client,
                clientState: {
                    ...client.clientState,
                    queries: client.clientState.queries.map(q => {
                        if (!q.state.data?.pages) return q
                        const truncated = truncateInfiniteData(q.state.data)
                        if (truncated === q.state.data) return q
                        return { ...q, state: { ...q.state, data: truncated } }
                    }),
                },
            }
            await set(cacheKey, JSON.parse(JSON.stringify(safe)))
        },
        restoreClient: () => get(cacheKey),
        removeClient: () => del(cacheKey),
    }
}

export function setupPersister(queryClient, orgId) {
    const cacheKey = `REACT_QUERY_OFFLINE_CACHE_${orgId}`
    return persistQueryClient({
        queryClient,
        persister: makePersister(cacheKey),
        maxAge: MAX_AGE,
        dehydrateOptions: {
            shouldDehydrateQuery,
        },
    })
}
